// Components==============
import Navigate from 'components/Layout/NavigationBox/components/Navigate/Navigate';
import Thermometer from 'components/UI/Thermometer/Thermometer';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import storeThermometer from 'actions/thermometer/store';
import retrieveAuthenticatedUser from 'actions/authentication/retrieve';
import { HomeContext } from 'screens/Home/Home';
import './ThermometerSequence.module.scss';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
// =========================

type ThermometerSequenceContextType = {
    results: ThermometerResult;
    setResults: React.Dispatch<React.SetStateAction<ThermometerResult>>;
    sequence: 'spelling' | 'lezen' | 'finished';
    isViewing: boolean;
};

export const ThermometerSequenceContext = createContext(
    {} as ThermometerSequenceContextType
);

export default function ThermometerSequence() {
    const [sequence, setSequence] = useState<'spelling' | 'lezen' | 'finished'>(
        'lezen'
    );

    const { openThermometer, setOpenThermometer } = useContext(HomeContext);
    const dispatch = useDispatch();

    useEffect(() => {
        if (openThermometer === '1a') {
            setSequence('lezen');
        } else if (openThermometer === '1b') {
            setSequence('spelling');
        }
    }, [openThermometer]);

    const thermometer = useSelector((state: ReduxStore) =>
        state.thermometer?.entities.allIds
            .map((id) => state.thermometer?.entities.byId[id])
            .find((t) => {
                if (openThermometer === '1a' || openThermometer === '1b') {
                    return t?.thermometer_number === 1;
                }
                return t?.thermometer_number === openThermometer;
            })
    );

    const [results, setResults] = useState<ThermometerResult>(
        thermometer
            ? {
                  thermometer_number:
                      openThermometer === '1b'
                          ? '1b'
                          : thermometer?.thermometer_number,
                  spelling_enjoy: thermometer.spelling_enjoy,
                  spelling_skill: thermometer.spelling_skill,
                  spelling_motivation: thermometer.spelling_motivation,
                  reading_enjoy: thermometer.reading_enjoy,
                  reading_skill: thermometer.reading_skill,
                  reading_motivation: thermometer.reading_motivation,
              }
            : {
                  thermometer_number: openThermometer,
                  spelling_enjoy: 0,
                  spelling_skill: 0,
                  spelling_motivation: 0,
                  reading_enjoy: 0,
                  reading_skill: 0,
                  reading_motivation: 0,
              }
    );
    const [showWarning, setShowWarning] = useState(false);

    const previousSequence = () => {
        if (openThermometer === '1a' || openThermometer === '1b') {
            return undefined;
        }
        return sequence === 'spelling' ? setSequence('lezen') : undefined;
    };

    const nextSequence = () => {
        if (openThermometer === '1a' || openThermometer === '1b') {
            return setSequence('finished');
        }
        return sequence === 'lezen'
            ? setSequence('spelling')
            : sequence === 'spelling'
            ? setSequence('finished')
            : undefined;
    };

    console.log(thermometer);

    const isViewing =
        openThermometer === '1b'
            ? thermometer?.spelling_enjoy > 0 ||
              thermometer?.spelling_skill > 0 ||
              thermometer?.spelling_motivation > 0
            : thermometer !== undefined;

    const spellingIsFilled =
        results.spelling_enjoy >= 1 &&
        results.spelling_motivation >= 1 &&
        results.spelling_skill >= 1;

    const readingIsFilled =
        results.reading_enjoy >= 1 &&
        results.reading_motivation >= 1 &&
        results.reading_skill >= 1;

    const canGoNext =
        (sequence === 'spelling' && spellingIsFilled) ||
        (sequence === 'lezen' && readingIsFilled);
    const canGoPrevious =
        sequence !== 'lezen' &&
        !(openThermometer !== '1a' || openThermometer !== '1b');

    useEffect(() => {
        if (sequence === 'finished') {
            if (isViewing) return setOpenThermometer(0);
            // @ts-ignore
            dispatch(storeThermometer(results))
                .then(() => {
                    dispatch(retrieveAuthenticatedUser());
                })
                .then(() => setOpenThermometer(0));
        }
    }, [sequence]);

    function checkShowWarning() {
        if (!isViewing) {
            return;
        }
        setShowWarning(true);
        setTimeout(() => {
            setShowWarning(false);
        }, 2000);
    }
    return (
        <ThermometerSequenceContext.Provider
            value={{ results, setResults, sequence, isViewing }}
        >
            <motion.div
                styleName="wrapper "
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <div styleName="top">
                    <span>
                        <h3>
                            {`${sequence[0].toUpperCase()}${sequence.slice(1)}`}
                            , wat vind jij ervan?
                        </h3>
                        {isViewing && <p>Deze opdracht is ingeleverd</p>}
                    </span>
                    <Navigate
                        next={canGoNext ? nextSequence : undefined}
                        previous={canGoPrevious ? previousSequence : undefined}
                        shouldAnimate={false}
                        canFinish={
                            (sequence === 'spelling' && spellingIsFilled) ||
                            (openThermometer === '1a' && readingIsFilled)
                        }
                    />
                </div>
                <div styleName="content" onClick={checkShowWarning}>
                    <Thermometer
                        text={`Hoe leuk vind ik ${sequence}?`}
                        type="enjoy"
                    />
                    <Thermometer
                        text={`Hoe goed vind ik mezelf in ${sequence}?`}
                        type="skill"
                    />
                    <Thermometer
                        text={`Hoeveel zin heb ik in het oefenen van ${sequence}?`}
                        type="motivation"
                    />
                </div>
                {showWarning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        styleName="warning "
                    >
                        <span>
                            <h3>Al ingeleverd</h3>
                            <div styleName="icon-wrapper">
                                <FontAwesomeIcon icon={faCheck} />
                            </div>
                        </span>
                        <p>
                            Je hebt dit al eerder ingevuld, daarom kun je dit nu
                            niet meer aanpassen.
                        </p>
                    </motion.div>
                )}
            </motion.div>
        </ThermometerSequenceContext.Provider>
    );
}
